import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  subscribeToClients,
  subscribeToAllObjects,
  subscribeToAllInspections,
} from '../utils/storage';
import CreateInspectionModal from './CreateInspectionModal';
import GeneralInfoModal from './GeneralInfoModal';
import LoadingScreen from './LoadingScreen';
import { SearchBar, EmptyState } from './templates';
import './InspectionsPage.css';
import './InspectionStatus.css';
import './Modal.css';

// ── Helpers ──────────────────────────────────────────────────────

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function isAssessmentInfoFilled(gi) {
  if (!gi) return false;
  return !!(
    gi.inspectionStandard &&
    gi.inspectionNumber   &&
    gi.startDate          &&
    gi.endDate            &&
    gi.accountManager     &&
    gi.leadNDTOperator
  );
}

// ─────────────────────────────────────────────────────────────────

export default function InspectionsPage() {
  const navigate = useNavigate();

  const [clients,      setClients]      = useState([]);
  const [allObjects,   setAllObjects]   = useState([]);
  const [allInspections, setAllInspections] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [pageReady,    setPageReady]    = useState(false);
  const [search,       setSearch]       = useState('');

  // Modal flow states
  // phase: null | 'selectObject' | 'fillAssessmentInfo' | 'addInspection'
  const [modalPhase,      setModalPhase]      = useState(null);
  const [selectedObject,  setSelectedObject]  = useState(null);

  // ── Data subscriptions ───────────────────────────────────────
  useEffect(() => {
    let clientsReady     = false;
    let objectsReady     = false;
    let inspectionsReady = false;

    function checkReady() {
      if (clientsReady && objectsReady && inspectionsReady) setLoading(false);
    }

    const u1 = subscribeToClients((list) => {
      setClients(list);
      clientsReady = true;
      checkReady();
    });
    const u2 = subscribeToAllObjects((list) => {
      setAllObjects(list);
      objectsReady = true;
      checkReady();
    });
    const u3 = subscribeToAllInspections((list) => {
      setAllInspections(list);
      inspectionsReady = true;
      checkReady();
    });

    return () => { u1(); u2(); u3(); };
  }, []);

  // ── Build display data ───────────────────────────────────────
  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));

  // Group inspections by objectId
  const inspectionsByObject = {};
  for (const insp of allInspections) {
    if (!inspectionsByObject[insp.objectId]) inspectionsByObject[insp.objectId] = [];
    inspectionsByObject[insp.objectId].push(insp);
  }

  // Build one group per object that has at least one inspection
  const groups = allObjects
    .filter((obj) => inspectionsByObject[obj.id]?.length > 0)
    .map((obj) => {
      const startDate    = obj.generalInfo?.startDate || '';
      const testSite     = clientMap[obj.clientId]?.name || '—';
      const insps        = [...(inspectionsByObject[obj.id] || [])].sort((a, b) =>
        (a.type || '').localeCompare(b.type || '')
      );
      return { obj, startDate, testSite, insps };
    })
    .sort((a, b) => {
      if (!a.startDate && !b.startDate) return 0;
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return a.startDate.localeCompare(b.startDate);
    });

  // ── Search filter ────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const filtered = q
    ? groups.filter((g) =>
        (g.obj.objectTag || '').toLowerCase().includes(q) ||
        g.testSite.toLowerCase().includes(q) ||
        g.insps.some((i) => (i.type || '').toLowerCase().includes(q))
      )
    : groups;

  // ── Modal flow handlers ──────────────────────────────────────
  function openAddInspection() {
    setSelectedObject(null);
    setModalPhase('selectObject');
  }

  function handleObjectSelected(obj) {
    setSelectedObject(obj);
    if (isAssessmentInfoFilled(obj.generalInfo)) {
      setModalPhase('addInspection');
    } else {
      setModalPhase('fillAssessmentInfo');
    }
  }

  function handleAssessmentInfoSaved() {
    // After GI filled in, proceed to inspection type picker
    setModalPhase('addInspection');
  }

  function handleInspectionCreated() {
    setModalPhase(null);
    setSelectedObject(null);
  }

  function closeModal() {
    setModalPhase(null);
    setSelectedObject(null);
  }

  // ── Loading screen ───────────────────────────────────────────
  if (!pageReady) {
    return (
      <LoadingScreen
        isLoading={loading}
        onDone={() => setPageReady(true)}
      />
    );
  }

  const totalCount = filtered.reduce((n, g) => n + g.insps.length, 0);

  return (
    <div className="page-container">

      {/* ── Toolbar ── */}
      <div className="toolbar">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search object, site, type…"
        />
        <button className="btn btn-primary" onClick={openAddInspection}>
          + Add Inspection
        </button>
      </div>

      {/* ── Empty state ── */}
      {filtered.length === 0 && (
        <EmptyState icon="🔬">
          {q ? (
            <p>No inspections match your search.</p>
          ) : (
            <>
              <h2>No inspections yet</h2>
              <p>Add the first inspection to an object to get started.</p>
              <button className="btn btn-primary" onClick={openAddInspection}>+ Add Inspection</button>
            </>
          )}
        </EmptyState>
      )}

      {/* ── Inspections table ── */}
      {filtered.length > 0 && (
        <div className="insp-table-wrapper">
          <table className="insp-main-table">
            <colgroup>
              <col className="col-idate" />
              <col className="col-iobj" />
              <col className="col-isite" />
              <col className="col-itype" />
              <col className="col-iarrow" />
            </colgroup>
            <thead>
              <tr>
                <th>Start Date</th>
                <th>Object ID ({filtered.length})</th>
                <th>Test Site</th>
                <th>Inspection Type ({totalCount})</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((group, gIdx) => {
                const { obj, startDate, testSite, insps } = group;
                const rowSpan = insps.length;
                const isLastGroup = gIdx === filtered.length - 1;
                const spanClass = `insp-cell-span${isLastGroup ? ' insp-span-last' : ''}`;
                const navTo = (insp) =>
                  navigate(`/client/${obj.clientId}/object/${obj.id}/inspection/${insp.id}`);

                return insps.map((insp, idx) => (
                  <tr
                    key={insp.id}
                    className={`insp-row${idx === 0 ? ' insp-group-first' : ' insp-sub-row'}${idx === rowSpan - 1 ? ' insp-group-last' : ''}`}
                  >
                    {/* First sub-row carries the shared data (rowspan) */}
                    {idx === 0 && (
                      <>
                        <td rowSpan={rowSpan} className={`insp-cell-date ${spanClass}`}>
                          {fmtDate(startDate)}
                        </td>
                        <td
                          rowSpan={rowSpan}
                          className={`insp-cell-obj insp-cell-obj-link ${spanClass}`}
                          onClick={() => navigate(`/client/${obj.clientId}/object/${obj.id}`)}
                        >
                          <span className="insp-obj-tag">{obj.objectTag || '—'}</span>
                        </td>
                        <td rowSpan={rowSpan} className={`insp-cell-site ${spanClass}`}>
                          {testSite}
                        </td>
                      </>
                    )}
                    <td className="insp-cell-type" onClick={() => navTo(insp)}>
                      <div className="insp-type-cell">
                        <div className="insp-status-dot-wrapper">
                          <span className="insp-status-dot" data-status={insp.effectiveStatus} />
                          <span className="insp-status-dot-tooltip">{insp.effectiveStatus}</span>
                        </div>
                        <span className="insp-type-badge">{insp.type}</span>
                      </div>
                    </td>
                    <td className="insp-cell-arrow" onClick={() => navTo(insp)}>›</td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Object selector modal ── */}
      {modalPhase === 'selectObject' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select Object</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <p className="confirm-text">Choose the object to add an inspection to.</p>

            {allObjects.length > 0 ? (
              <div className="site-select-list">
                {[...allObjects]
                  .sort((a, b) => (a.objectTag || '').localeCompare(b.objectTag || ''))
                  .map((obj) => {
                    const site  = clientMap[obj.clientId]?.name || '—';
                    const hasAssessmentInfo = isAssessmentInfoFilled(obj.generalInfo);
                    return (
                      <div
                        key={obj.id}
                        className="site-select-item"
                        onClick={() => handleObjectSelected(obj)}
                      >
                        <div style={{ flex: 1 }}>
                          <div className="site-select-item-name">{obj.objectTag || '—'}</div>
                          <div className="site-select-item-meta">
                            {obj.type || '—'} · {site}
                            {!hasAssessmentInfo && (
                              <span className="insp-gi-warn"> · Assessment info required</span>
                            )}
                          </div>
                        </div>
                        <span className="site-select-item-arrow">›</span>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="site-select-empty">
                <div className="site-select-empty-icon">📦</div>
                <p>No objects available. Create an object first.</p>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              {allObjects.length === 0 && (
                <button className="btn btn-primary" onClick={() => { closeModal(); navigate('/objects'); }}>
                  Go to Objects
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Fill General Info modal ── */}
      {modalPhase === 'fillAssessmentInfo' && selectedObject && (
        <GeneralInfoModal
          object={selectedObject}
          onClose={closeModal}
          onSaved={handleAssessmentInfoSaved}
        />
      )}

      {/* ── Add Inspection type picker modal ── */}
      {modalPhase === 'addInspection' && selectedObject && (
        <CreateInspectionModal
          objectId={selectedObject.id}
          existingTypes={allInspections
            .filter((i) => i.objectId === selectedObject.id)
            .map((i) => i.type)}
          objectTag={selectedObject.objectTag}
          siteName={clientMap[selectedObject.clientId]?.name}
          onClose={closeModal}
          onCreated={handleInspectionCreated}
        />
      )}

    </div>
  );
}
